// Laser pointer trail renderer shared by the audience view and the speaker echo.
//
// The goal is "light on a screen", not "a red tube". A tube look comes from an
// opaque body with hard edges and constant brightness, so this renderer:
//   1. smooths raw samples with a centripetal Catmull-Rom spline and resamples
//      them densely (timestamps interpolated along the curve) – no beads;
//   2. fades the trail by OPACITY along its age, not only by width. Per-sample
//      alpha is done with nested age bands: band k is one polyline covering
//      every sample younger than k/N, stroked once with a solved alpha so the
//      stacked result follows a smooth target curve. Each band is a single
//      stroke, so there are no overlap beads or seams;
//   3. adds a white-hot core that only survives near the head (it decays much
//      faster than the red body), like a beam fading from hot to dim;
//   4. builds the beam's cross-section from several translucent layers of
//      decreasing width, so the edge falls off softly instead of reading as a
//      hard-edged cable;
//   5. renders trail + head into an offscreen buffer and adds two bloom
//      passes computed at low resolution (cheap blur), so the glow follows
//      the faded shape;
//   6. lets the head shimmer very slightly, as a real laser spot does.

export type LaserPoint = { x: number; y: number; t: number };

export const LASER_TRAIL_MS = 650;
export const LASER_CORE_RATIO = 0.005; // fraction of slide width

type Sample = { x: number; y: number; t: number };

const HEAD_FADE_START = 0.6; // fraction of LASER_TRAIL_MS before the idle head starts fading
const BANDS = 16;
const SHADOW_SHIFT = 20000; // draw off-canvas and shift only the shadow back in

export function pruneLaserTrail(trail: LaserPoint[], now: number): LaserPoint[] {
  let start = 0;
  while (start < trail.length && now - trail[start].t >= LASER_TRAIL_MS) {
    start++;
  }
  return start === 0 ? trail : trail.slice(start);
}

function dedupe(trail: LaserPoint[], w: number, h: number, minDist: number): Sample[] {
  const out: Sample[] = [];
  for (const p of trail) {
    const s = { x: p.x * w, y: p.y * h, t: p.t };
    const last = out[out.length - 1];
    if (last && Math.hypot(s.x - last.x, s.y - last.y) < minDist) {
      // keep the newest timestamp/position so the head stays exact
      out[out.length - 1] = s;
    } else {
      out.push(s);
    }
  }
  return out;
}

// Centripetal Catmull-Rom (alpha = 0.5) – avoids cusps and overshoot on sharp turns.
function catmullRom(points: Sample[], spacing: number): Sample[] {
  if (points.length < 2) {
    return points.slice();
  }
  const out: Sample[] = [points[0]];
  const get = (i: number) => points[Math.max(0, Math.min(points.length - 1, i))];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = get(i - 1);
    const p1 = get(i);
    const p2 = get(i + 1);
    const p3 = get(i + 2);
    const segLen = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const steps = Math.max(1, Math.ceil(segLen / spacing));

    const knot = (a: Sample, b: Sample) =>
      Math.max(Math.sqrt(Math.hypot(b.x - a.x, b.y - a.y)), 1e-4);
    const t0 = 0;
    const t1 = t0 + knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);

    for (let s = 1; s <= steps; s++) {
      const u = s / steps;
      const t = t1 + (t2 - t1) * u;
      const lerp = (a: Sample, b: Sample, ta: number, tb: number) => {
        const k = (t - ta) / (tb - ta);
        return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
      };
      const a1 = lerp(p0, p1, t0, t1);
      const a2 = lerp(p1, p2, t1, t2);
      const a3 = lerp(p2, p3, t2, t3);
      const b1 = {
        x: a1.x + (a2.x - a1.x) * ((t - t0) / (t2 - t0)),
        y: a1.y + (a2.y - a1.y) * ((t - t0) / (t2 - t0)),
      };
      const b2 = {
        x: a2.x + (a3.x - a2.x) * ((t - t1) / (t3 - t1)),
        y: a2.y + (a3.y - a2.y) * ((t - t1) / (t3 - t1)),
      };
      const k = (t - t1) / (t2 - t1);
      out.push({
        x: b1.x + (b2.x - b1.x) * k,
        y: b1.y + (b2.y - b1.y) * k,
        t: p1.t + (p2.t - p1.t) * u,
      });
    }
  }
  return out;
}

// Stroke the trail so that a pixel at age `a` ends up with alpha ≈ target(a).
// Bands are nested: band k is ONE polyline from the head back to the oldest
// sample of band k, stroked once (so it never overlaps itself) with a width
// that matches that age. Younger pixels are covered by more bands; for one
// colour, source-over stacking gives 1 - Π(1 - α_j), so solving for α_b gives
// (1 - α_b) = (1 - T_b) / (1 - T_{b+1}). Because older bands are thinner, the
// beam edge only receives the outer bands and falls off softly.
function strokeByAge(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  bandOf: number[],
  radiusAt: (age: number) => number,
  rgb: string,
  target: (age: number) => number,
) {
  const T = (b: number) => (b >= BANDS ? 0 : Math.min(0.985, target((b + 0.5) / BANDS)));
  const last = samples.length - 1;
  const path = new Path2D();
  path.moveTo(samples[last].x, samples[last].y);
  let i = last;
  for (let k = 0; k < BANDS; k++) {
    // samples are ordered oldest → newest; extend the polyline backwards
    while (i > 0 && bandOf[i - 1] <= k) {
      i--;
      path.lineTo(samples[i].x, samples[i].y);
    }
    if (i === last) {
      continue;
    }
    const alpha = 1 - (1 - T(k)) / (1 - T(k + 1));
    const width = 2 * radiusAt((k + 1) / BANDS);
    if (alpha <= 0.002 || width <= 0.1) {
      continue;
    }
    ctx.lineWidth = width;
    ctx.strokeStyle = `rgba(${rgb},${alpha.toFixed(4)})`;
    ctx.stroke(path);
  }
}

const canvases = new Map<string, HTMLCanvasElement>();

// Reusable scratch canvas, grown as needed and cleared on every use.
function scratch(key: string, width: number, height: number): CanvasRenderingContext2D | null {
  let canvas = canvases.get(key);
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvases.set(key, canvas);
  }
  if (canvas.width < width || canvas.height < height) {
    canvas.width = Math.max(canvas.width, Math.ceil(width));
    canvas.height = Math.max(canvas.height, Math.ceil(height));
  }
  const c = canvas.getContext('2d');
  if (!c) {
    return null;
  }
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalAlpha = 1;
  c.globalCompositeOperation = 'source-over';
  c.shadowColor = 'transparent';
  c.shadowBlur = 0;
  c.shadowOffsetX = 0;
  c.clearRect(0, 0, canvas.width, canvas.height);
  return c;
}

// Cross-section of the beam, outside → in. Several translucent layers of
// decreasing width give a soft, light-like falloff instead of a hard-edged
// tube; the white-hot centre decays much faster than the red so it only
// survives right behind the head.
const PROFILE: { scale: number; rgb: string; target: (age: number) => number }[] = [
  { scale: 1.0, rgb: '255,0,0', target: (a) => 0.26 * Math.pow(1 - a, 1.2) },
  { scale: 0.7, rgb: '255,20,8', target: (a) => 0.5 * Math.pow(1 - a, 1.3) },
  { scale: 0.45, rgb: '255,80,50', target: (a) => 0.7 * Math.pow(1 - a, 1.7) },
  { scale: 0.22, rgb: '255,242,235', target: (a) => 0.95 * Math.pow(1 - a, 3) },
];

// Low-resolution bloom: downscale, blur there (cheap), upscale back.
function bloom(
  ctx: CanvasRenderingContext2D,
  src: HTMLCanvasElement,
  ox: number,
  oy: number,
  bw: number,
  bh: number,
  factor: number,
  blur: number,
  color: string,
) {
  const gw = Math.ceil(bw / factor);
  const gh = Math.ceil(bh / factor);
  const g = scratch(`bloom${factor}`, gw, gh);
  const glow = canvases.get(`bloom${factor}`);
  if (!g || !glow) {
    return;
  }
  // draw off-canvas and shift only the shadow back in: pure blurred glow
  g.shadowOffsetX = SHADOW_SHIFT;
  g.shadowColor = color;
  g.shadowBlur = blur / factor;
  g.drawImage(src, 0, 0, bw, bh, -SHADOW_SHIFT, 0, gw, gh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(glow, 0, 0, gw, gh, ox, oy, gw * factor, gh * factor);
}

export function drawLaserTrail(
  ctx: CanvasRenderingContext2D,
  trail: LaserPoint[],
  w: number,
  h: number,
  now: number,
): void {
  if (trail.length === 0) {
    return;
  }
  const core = Math.max(LASER_CORE_RATIO * w, 2);
  const head = trail[trail.length - 1];
  const headAge = Math.min(1, (now - head.t) / LASER_TRAIL_MS);
  const headAlpha =
    headAge <= HEAD_FADE_START
      ? 1
      : Math.max(0, 1 - (headAge - HEAD_FADE_START) / (1 - HEAD_FADE_START));

  const raw = dedupe(trail, w, h, Math.max(core * 0.25, 1));
  const samples = raw.length >= 2 ? catmullRom(raw, Math.max(core * 0.6, 1)) : [];
  const ages = samples.map((s) => Math.min(1, Math.max(0, (now - s.t) / LASER_TRAIL_MS)));
  const bandOf = ages.map((a) => Math.min(BANDS - 1, Math.floor(a * BANDS)));
  // the beam narrows gently; opacity does most of the fading
  const radiusAt = (a: number) => core * (0.25 + 0.5 * Math.pow(1 - a, 0.8));
  const maxR = radiusAt(0);

  // bounding box of everything we paint, padded for the bloom, in overlay pixels
  const hx = head.x * w;
  const hy = head.y * h;
  const haloR = core * 3.2;
  let minX = hx - haloR;
  let minY = hy - haloR;
  let maxX = hx + haloR;
  let maxY = hy + haloR;
  for (const s of samples) {
    minX = Math.min(minX, s.x - maxR);
    minY = Math.min(minY, s.y - maxR);
    maxX = Math.max(maxX, s.x + maxR);
    maxY = Math.max(maxY, s.y + maxR);
  }
  const pad = Math.ceil(core * 8);
  const ox = Math.floor(minX) - pad;
  const oy = Math.floor(minY) - pad;
  const bw = Math.ceil(maxX) - ox + pad;
  const bh = Math.ceil(maxY) - oy + pad;
  const bctx = scratch('beam', bw, bh);
  const beam = canvases.get('beam');
  if (!bctx || !beam) {
    return;
  }
  bctx.translate(-ox, -oy);

  if (samples.length >= 2) {
    bctx.lineCap = 'round';
    bctx.lineJoin = 'round';
    for (const layer of PROFILE) {
      strokeByAge(bctx, samples, bandOf, (a) => radiusAt(a) * layer.scale, layer.rgb, layer.target);
    }
  }

  if (headAlpha > 0) {
    // tiny shimmer, like speckle on a real laser spot
    const shimmer = 0.94 + Math.random() * 0.06;
    bctx.globalAlpha = headAlpha;
    const halo = bctx.createRadialGradient(hx, hy, 0, hx, hy, haloR * shimmer);
    halo.addColorStop(0, 'rgba(255, 30, 20, 0.55)');
    halo.addColorStop(0.25, 'rgba(255, 0, 0, 0.25)');
    halo.addColorStop(1, 'rgba(255, 0, 0, 0)');
    bctx.fillStyle = halo;
    bctx.beginPath();
    bctx.arc(hx, hy, haloR, 0, Math.PI * 2);
    bctx.fill();

    const dotR = core;
    const dot = bctx.createRadialGradient(hx, hy, 0, hx, hy, dotR);
    dot.addColorStop(0, 'rgba(255, 255, 255, 1)');
    dot.addColorStop(0.3, `rgba(255, 225, 215, ${shimmer})`);
    dot.addColorStop(0.6, 'rgba(255, 60, 40, 0.9)');
    dot.addColorStop(1, 'rgba(255, 0, 0, 0)');
    bctx.fillStyle = dot;
    bctx.beginPath();
    bctx.arc(hx, hy, dotR, 0, Math.PI * 2);
    bctx.fill();
    bctx.globalAlpha = 1;
  }

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  // wide, faint haze and a tighter glow, both following the faded beam
  bloom(ctx, beam, ox, oy, bw, bh, 8, core * 5, 'rgba(255, 0, 0, 0.6)');
  bloom(ctx, beam, ox, oy, bw, bh, 2, core * 1.2, 'rgba(255, 20, 10, 0.85)');
  ctx.drawImage(beam, 0, 0, bw, bh, ox, oy, bw, bh);
  ctx.restore();
}
